/**
 * DiversificationTab — the "Diversificazione" area of the results.
 *
 * Five levels, in this reading order:
 *   Portafoglio   — aggregated exposure, concentration and real redundancy with
 *                   the weights of the Allocazione tab (Portfolio Impact)
 *   ETF Overlap   — the structural pairwise heatmap (Phase 2)
 *   Confronta ETF — structural reading of a group of 2–10 ETFs
 *   Contributo ETF — Leave-One-Out marginal contribution of every ETF (Phase R2)
 *   What-if       — one user-chosen Add or Replace, simulated read-only (Phase 5)
 *
 * The dimension selector is shared by the levels, and the pair drill-down
 * of Phase 2 is reachable from all of them. No synthetic "overall" number: the
 * 0–100 Diversification Score (Phase 4) lives in the Portafoglio level as its
 * own panel, above the detailed analyses.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, Layers } from "lucide-react";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { EmptyState } from "@/components/system/states";
import { Tabs } from "@/components/system/Tabs";
import { OverlapMatrix } from "./OverlapMatrix";
import { OverlapPairDialog } from "./OverlapPairDialog";
import { PortfolioPanel } from "./PortfolioPanel";
import { ScorePanel } from "./ScorePanel";
import { GroupComparePanel } from "./GroupComparePanel";
import { WhatIfPanel } from "./WhatIfPanel";
import { MarginalContributionPanel } from "./MarginalContributionPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVELS = [
  { id: "portfolio", label: "Portafoglio", testId: "level-portfolio" },
  { id: "overlap", label: "ETF Overlap", testId: "level-overlap" },
  { id: "group", label: "Confronta ETF", testId: "level-group" },
  { id: "contribution", label: "Contributo ETF", testId: "level-contribution" },
  { id: "whatif", label: "What-if", testId: "level-whatif" },
];

export function DiversificationTab({
  sessionId, portfolioIsins = [], allocationSignature = "",
}) {
  const [level, setLevel] = useState("portfolio");
  const [dimension, setDimension] = useState("entity");
  const [matrix, setMatrix] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pair, setPair] = useState(null);
  const [knownEtfs, setKnownEtfs] = useState([]);
  // The element that opened the drill-down, so closing it gives the focus back.
  const pairTrigger = useRef(null);
  // Leave-One-Out readings already obtained in this session, per dimension: a
  // 25-ETF batch costs seconds, reopening the level must not pay it again. The
  // cache dies with the analysis view, and the backend keys its own cache on the
  // content of the inputs, so a stale reading cannot survive a change.
  // Keys are `session|dimension|allocation`, so a reading can never survive a
  // reweight in the Allocazione tab; the map dies with the analysis view.
  const redundancyCache = useRef(new Map()).current;

  const openPair = (next) => {
    pairTrigger.current = document.activeElement;
    setPair(next);
  };

  const closePair = () => {
    setPair(null);
    const trigger = pairTrigger.current;
    pairTrigger.current = null;
    if (trigger && document.contains(trigger)) {
      requestAnimationFrame(() => trigger.focus());
    }
  };

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/diversification/overlap/${sessionId}`, { params: { dimension } })
      .then(({ data }) => { if (!cancelled) setMatrix(data); })
      .catch(() => {
        if (!cancelled) {
          setMatrix(null);
          setError("Non è stato possibile calcolare la sovrapposizione per questa analisi.");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sessionId, dimension]);

  // Catalogue of candidates for the What-if level, loaded once.
  useEffect(() => {
    if (level !== "whatif" || knownEtfs.length) return undefined;
    let cancelled = false;
    axios
      .get(`${API}/known-etfs`)
      .then(({ data }) => {
        if (!cancelled) {
          setKnownEtfs(
            (data?.etfs || [])
              .filter((etf) => etf.isin && (etf.holdings_count || 0) > 0)
              .map((etf) => ({
                isin: etf.isin,
                ticker: etf.ticker || "",
                name: etf.etf_name || etf.ticker || etf.isin,
                holdings_updated_at: etf.holdings_updated_at,
              })),
          );
        }
      })
      .catch(() => { /* non-fatal: the panel shows an empty candidate list */ });
    return () => { cancelled = true; };
  }, [level, knownEtfs.length]);

  const dimensions = matrix?.available_dimensions || [];
  const current = dimensions.find((d) => d.dimension === dimension);
  const subjects = matrix?.subjects || [];

  const selectPairByIndex = (i, j) => {
    if (!subjects[i] || !subjects[j]) return;
    openPair({ a: subjects[i].key, b: subjects[j].key });
  };

  return (
    <div className="space-y-4" data-testid="diversification-tab">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-content">Diversificazione</h2>
          <p className="text-sm text-content-muted max-w-2xl">
            Cinque letture della stessa analisi: come è composto il tuo portafoglio e quanto
            è realmente duplicato, quanto si assomigliano gli ETF fra loro, cosa condivide
            un gruppo di ETF, quanto cambierebbe il portafoglio senza ciascun ETF e cosa
            cambierebbe se aggiungessi o sostituissi un ETF. La misura è strutturale, non
            è una correlazione di prezzo.
          </p>
        </div>
        <div className="min-w-[220px]">
          <label
            htmlFor="diversification-dimension"
            className="block text-label text-content-muted mb-1"
          >
            Dimensione
          </label>
          <Select value={dimension} onValueChange={setDimension}>
            <SelectTrigger
              id="diversification-dimension"
              className="h-10"
              data-testid="dimension-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(dimensions.length ? dimensions : [{ dimension: "entity", label: "Società (canonical)" }])
                .map((info) => (
                  <SelectItem
                    key={info.dimension}
                    value={info.dimension}
                    data-testid={`dimension-option-${info.dimension}`}
                  >
                    {info.label}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <Tabs
        testId="diversification-levels"
        ariaLabel="Livelli della diversificazione"
        value={level}
        onChange={setLevel}
        items={LEVELS}
      />

      {current?.description && (
        <p className="text-label text-content-muted" data-testid="dimension-description">
          {current.description}
        </p>
      )}

      {level === "portfolio" && (
        <>
          <ScorePanel sessionId={sessionId} />
          <PortfolioPanel
            sessionId={sessionId}
            dimension={dimension}
            onSelectPair={openPair}
          />
        </>
      )}

      {level === "overlap" && (
        <>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-content-muted" data-testid="diversification-loading">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Calcolo della matrice…
            </div>
          )}
          {error && !loading && (
            <p className="text-sm text-danger" data-testid="diversification-error">{error}</p>
          )}
          {!loading && !error && matrix && subjects.length < 2 && (
            <div data-testid="diversification-empty">
              <EmptyState
                icon={Layers}
                title="Serve più di un ETF"
                description="La sovrapposizione confronta gli ETF a coppie: aggiungi almeno un secondo ETF all'analisi."
              />
            </div>
          )}
          {!loading && !error && matrix && subjects.length >= 2 && (
            <OverlapMatrix matrix={matrix} onSelectPair={selectPairByIndex} />
          )}
        </>
      )}

      {level === "group" && (
        subjects.length >= 2 ? (
          <GroupComparePanel
            sessionId={sessionId}
            dimension={dimension}
            subjects={subjects}
            onSelectPair={openPair}
          />
        ) : (
          <div data-testid="group-empty">
            <EmptyState
              icon={Layers}
              title="Serve più di un ETF"
              description="Il confronto di gruppo richiede almeno 2 ETF nell'analisi."
            />
          </div>
        )
      )}

      {level === "contribution" && (
        <MarginalContributionPanel
          sessionId={sessionId}
          dimension={dimension}
          dimensionLabel={current?.label || matrix?.dimension_label}
          cache={redundancyCache}
          allocationSignature={allocationSignature}
        />
      )}

      {level === "whatif" && (
        <WhatIfPanel
          sessionId={sessionId}
          dimension={dimension}
          subjects={subjects}
          knownEtfs={knownEtfs}
          portfolioIsins={portfolioIsins}
        />
      )}

      <OverlapPairDialog
        open={Boolean(pair)}
        onOpenChange={(open) => { if (!open) closePair(); }}
        sessionId={sessionId}
        dimension={dimension}
        pair={pair}
      />
    </div>
  );
}

export default DiversificationTab;
